import { ADMIN_USER_STATES } from '@nestjs-boilerplate/types';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import type { AdminUserFilters } from '@/features/admin/api/users';
import { AdminUsersPage } from '@/features/admin/components/admin-users-page';
import { validatePaginationSearch } from '@/lib/pagination-search';
import { manyOf, manyStrings } from '@/lib/query-params';

/**
 * Reads the filter set out of the URL.
 *
 * Search params rather than component state so a filtered list survives a
 * drill-down into a user and back — the normal flow on this screen.
 *
 * Paging is delegated to the shared validator the other admin lists use, so a
 * mistyped page number falls back rather than becoming a failed request.
 */
function validateSearch(search: Record<string, unknown>): AdminUserFilters {
  const filters: AdminUserFilters = {};

  if (typeof search.search === 'string' && search.search) filters.search = search.search;
  // Both filters accept several values at once, comma-separated. An
  // unrecognised value is dropped rather than rejected: nothing here can report
  // an error to the reader, so a mistyped link has to degrade to a wider list.
  const role = manyStrings(search.role);
  if (role) filters.role = role;
  const state = manyOf(ADMIN_USER_STATES, search.state);
  if (state) filters.state = state;
  // Date bounds are kept only in the `YYYY-MM-DD` form the picker produces and
  // the API expects. Anything else is dropped rather than forwarded, so a
  // hand-edited URL degrades to an unfiltered list instead of a 400.
  if (typeof search.from === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(search.from)) {
    filters.from = search.from;
  }
  if (typeof search.to === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(search.to)) {
    filters.to = search.to;
  }
  // Paging goes through the shared validator, so this screen bounds it the same
  // way every other list does. A bare `typeof === 'number'` let page=0, page=-3,
  // page=1.5 and pageSize=5000 straight through to an API that rejects all four.
  const paging = validatePaginationSearch(search);
  if (paging.page !== undefined) filters.page = paging.page;
  if (paging.pageSize !== undefined) filters.pageSize = paging.pageSize;
  if (
    search.sortBy === 'email' ||
    search.sortBy === 'name' ||
    search.sortBy === 'company' ||
    search.sortBy === 'role' ||
    search.sortBy === 'createdAt' ||
    search.sortBy === 'balance'
  ) {
    filters.sortBy = search.sortBy;
  }
  if (search.sortOrder === 'asc' || search.sortOrder === 'desc') {
    filters.sortOrder = search.sortOrder;
  }

  return filters;
}

export const Route = createFileRoute('/admin/users/')({
  validateSearch,
  component: AdminUsersRoute,
});

function AdminUsersRoute() {
  const filters = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  return (
    <AdminUsersPage
      filters={filters}
      onFiltersChange={(next) => navigate({ search: next, replace: true })}
    />
  );
}
